import PropTypes from "prop-types";
import { useEffect, useRef } from "react";
import { Box } from "@mui/material";

// A robust YouTube player using the official IFrame Player API. Non-YouTube
// URLs fall back to a plain iframe.

const MAX_RETRIES = 4;

// Returns true if the URL host looks like YouTube.
function isYouTubeUrl(url) {
  const host = (url.hostname || "").toLowerCase();
  return (
    host.includes("youtube.com") ||
    host.includes("youtu.be") ||
    host.includes("youtube-nocookie.com")
  );
}

// Attempts to extract a video ID from the various YouTube URL formats.
export function extractYouTubeVideoId(rawUrl) {
  let url;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }

  // 1) query param "v"
  const v = url.searchParams.get("v");
  if (v) return v;

  const parts = url.pathname.split("/").filter(Boolean);

  // 2) youtu.be short link -> first path segment
  if ((url.hostname || "").toLowerCase().includes("youtu.be")) {
    return parts[0] || null;
  }

  // 3) /embed/<id> or /v/<id> or /shorts/<id>
  const idx = parts.findIndex(
    (p) => p === "embed" || p === "v" || p === "shorts",
  );
  if (idx !== -1 && parts[idx + 1]) return parts[idx + 1];

  return null;
}

// Loads the IFrame Player API script once and resolves when window.YT is ready.
let apiPromise = null;
function loadYouTubeApi() {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (apiPromise) return apiPromise;
  apiPromise = new Promise((resolve, reject) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (prev) prev();
      resolve(window.YT);
    };
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    tag.onerror = () => {
      apiPromise = null; // allow a later attempt to retry
      tag.remove();
      reject(new Error("Failed to load YouTube IFrame API"));
    };
    document.head.appendChild(tag);
  });
  return apiPromise;
}

export const YouTubePlayer = ({
  url,
  autoplay = true,
  mute = true,
  onEnded,
  onError,
}) => {
  // References
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const retriesRef = useRef(0);
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  let parsed = null;
  try {
    parsed = new URL(url);
  } catch {
    parsed = null;
  }
  const videoId =
    parsed && isYouTubeUrl(parsed) ? extractYouTubeVideoId(url) : null;
  const isDirectVideo = /\.(mp4|webm|mov|m3u8)(\?|$)/i.test(url);

  useEffect(() => {
    if (!videoId) return undefined;
    let cancelled = false;
    retriesRef.current = 0;

    loadYouTubeApi()
      .then((YT) => {
        if (cancelled || !containerRef.current) return;
        // YT.Player replaces the element it's given, so hand it a throwaway child
        // and let React keep owning containerRef.
        const holder = document.createElement("div");
        holder.style.width = "100%";
        holder.style.height = "100%";
        containerRef.current.appendChild(holder);

        playerRef.current = new YT.Player(holder, {
          width: "100%",
          height: "100%",
          videoId,
          playerVars: {
            playsinline: 1,
            autoplay: autoplay ? 1 : 0,
            mute: mute ? 1 : 0,
            rel: 0,
          },
          events: {
            onStateChange: (event) => {
              if (event.data === YT.PlayerState.ENDED && onEndedRef.current) {
                onEndedRef.current();
              }
            },
            onError: () => {
              if (retriesRef.current < MAX_RETRIES) {
                retriesRef.current += 1;
                try {
                  playerRef.current.loadVideoById(videoId);
                  return;
                } catch {
                  // retry threw — fall through and report the failure
                }
              }
              onErrorRef.current?.();
            },
          },
        });
      })
      .catch(() => {
        if (!cancelled) onErrorRef.current?.();
      });

    return () => {
      cancelled = true;
      if (playerRef.current && playerRef.current.destroy) {
        try {
          playerRef.current.destroy();
        } catch {
          // ignore teardown errors
        }
        playerRef.current = null;
      }
    };
  }, [videoId, autoplay, mute]);

  return (
    <Box sx={{ width: "100%", height: "100%", backgroundColor: "#000" }}>
      {videoId ? (
        <Box ref={containerRef} sx={{ width: "100%", height: "100%" }} />
      ) : isDirectVideo ? (
        // Direct video with playback event handling available
        <Box
          component="video"
          src={url}
          autoPlay={autoplay}
          muted={mute}
          playsInline
          controls
          controlsList="nodownload noremoteplayback"
          disablePictureInPicture
          onEnded={() => onEndedRef.current?.()}
          onError={() => onErrorRef.current?.()}
          sx={{ width: "100%", height: "100%" }}
        />
      ) : (
        // non-YouTube URL and also non-direct video
        <Box
          component="iframe"
          src={url}
          title="Tutorial video"
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          sx={{ width: "100%", height: "100%", border: 0 }}
        />
      )}
    </Box>
  );
};

YouTubePlayer.propTypes = {
  url: PropTypes.string.isRequired,
  autoplay: PropTypes.bool,
  mute: PropTypes.bool,
  onEnded: PropTypes.func,
  onError: PropTypes.func,
};
